import React from 'react';
import { connect } from 'react-redux';
import { Row, Col } from 'antd';
import { withRouter } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import axios from 'axios';
import * as XLSX from 'xlsx';

import CostCodeCard from './costcodeCard';
import CostCodeAddView from './CostCodeAddView';
import CostCodeEditView from './CostCodeEditView';
import {
  archiveCostCode,
  bulkArchiveCostCodes,
  createCostCodes,
  updateCostCode,
  deleteCostCodeById,
  bulkDeleteCostCodes,
} from './state/costcodes.actions';
import { createNuxEntry } from '../nux/state/nux.actions';
import SideList from '../common/SideList';
import SideListSearchHeader from '../common/SideListSearchHeader';
import SideListText from '../common/SideList/SideListText';
import CardGrid from '../common/cardGrid/cardGrid';
import CustomConfirmModal from '../common/modals/CustomConfirmModal';
import WarningHeader from '../common/text/WarningHeader';

import sortByString, { search, getHighestNumber } from '../helpers/helpers';
import { request } from '../helpers/requests';
import Analytics from '../helpers/Analytics';
import FilterDropdown from '../common/FilterDropdown';
import {
  getDefaultFilters,
  getFilterData,
  searchCostcodes,
  searchTieBreaker,
} from './costcodeFilter';
import createCostcodeSortFilterController from './costcodeSortFilterController';

import Permissions from '../auth/Permissions';

import {
  COSTCODE_PROJECT_SPECIFIC_TYPE,
} from '../nux/nux.constants';
import { columnsToDisplay } from './costcodesList';
import { formatCustomFieldCreatePayload, getCostcodeUpdatePayload } from '../helpers/costcodeHelpers';

const FIELDS_BY_STEP = [
  ['code', 'name', 'description', 'categoryIds'],
];

const renderItem = ({
  name,
  number,
}) => (
  <Row
    type="flex"
    align="middle"
    style={{
      width: '100%',
      pointerEvents: 'all',
      cursor: 'pointer',
      height: 30,
    }}
  >
    <SideListText
      text={number ? `${number} - ${name}` : name}
    />
  </Row>
);

class CostCodeProjects extends React.Component {
  constructor(props) {
    super(props);

    this.sortFilter = createCostcodeSortFilterController({
      storageKeyText: 'ccProjectsSortText',
      storageKeyActive: 'ccProjectsSortActive',
      displayStateKey: 'costcodes',
      getFetchedCodes: () => this.state.fetchedCodes,
      getArchivedLoaded: () => this.state.archivedLoaded,
      setState: (update) => this.setState(update),
      onFetchArchived: () => {
        const { selectedProject } = this.state;
        this.fetchProjectCostcodes(selectedProject, { archivedOnly: true });
      },
    });

    this.state = {
      projects: [],
      fetchedCodes: [],
      costcodes: [],
      globalCodes: [],
      selectedDivisionIds: new Set(),
      isListView: window.localStorage.getItem('ccViewTypeProjects'),
      errors: {},
      customFields: [],
      initializedCustomCategoryFilters: false,
      archivedLoaded: false,
      ...this.sortFilter.getInitialState(),
    };

    this.onSearch = this.searchList.bind(this);
    this.onProjectSelected = this.projectSelected.bind(this);
    this.onCreateCostCode = this.createCostCodeWithProject.bind(this);
    this.onUpdate = this.update.bind(this);
    this.onArchive = this.archiveCostcode.bind(this);
    this.onDelete = this.deleteCostcode.bind(this);
    this.onExportCSV = this.exportCSV.bind(this);
    this.onExportExcel = this.exportExcel.bind(this);
  }

  componentDidMount() {
    const {
      createNuxEntry,
      nux = new Set(),
      location: {
        state: {
          targetId: locationTargetId,
          projectId: locationProjectId,
        } = {},
      } = {},
      projects = [],
      selectedDivisionIds = new Set(),
      t,
      setBreadcrumb,
    } = this.props;

    this.setState({
      selectedDivisionIds,
    });

    setBreadcrumb([{
      text: 'Cost Codes',
      icon: 'share-alt',
    }, {
      text: `${t('Project')} Specific`,
    }]);
    this.setInitialProjectState();
    if (!nux.has(COSTCODE_PROJECT_SPECIFIC_TYPE)) {
      createNuxEntry(COSTCODE_PROJECT_SPECIFIC_TYPE);
    }

    if (locationTargetId) {
      if (locationProjectId) {
        const ourProject = projects.find((project) => project.id === locationProjectId);
        if (ourProject) {
          this.setState({
            selectedProject: ourProject,
          });
        }
      }
      if (this.cardRef && locationTargetId) {
        this.cardRef.targetItem = { id: locationTargetId };
      }
    }
  }

  componentDidUpdate(prevProps) {
    const {
      projects = [],
      selectedDivisionIds = new Set(),
      categoriesIdMap = {},
    } = this.props;
    const {
      projects: prevProjects = [],
      selectedDivisionIds: prevSelectedDivisionIds = [],
      categoriesIdMap: prevCategoriesIdMap = {},
    } = prevProps;

    const {
      initializedCustomCategoryFilters,
      projects: stateProjects,
    } = this.state;

    if (selectedDivisionIds !== prevSelectedDivisionIds) {
      this.setState({ archivedLoaded: false });
      this.updateProjectState(projects, selectedDivisionIds);
    }

    if (projects !== prevProjects || !stateProjects) {
      this.setInitialProjectState();
    }

    if (prevCategoriesIdMap !== categoriesIdMap && !initializedCustomCategoryFilters) {
      this.sortFilter.filter.setDefaultFilters(getDefaultFilters(categoriesIdMap));
      this.setState({ initializedCustomCategoryFilters: true });
      this.sortFilter.applyFilters();
    }
  }

  setInitialProjectState() {
    const {
      projects = [],
    } = this.props;

    this.projects = projects.sort(sortByString('name'));
    this.setState({
      projects,
    });

    const { selectedProject: currentSelectedProject } = this.state;
    if (!currentSelectedProject) {
      const [firstProject] = this.projects;
      this.setState({
        selectedProject: firstProject,
      });
      this.fetchProjectCostcodes(firstProject);
      this.fetchGlobalCodesForDivision(firstProject?.divisionId);
    }
  }

  async fetchProjectCostcodes(project, { archivedOnly = false } = {}) {
    if (!project?.id) {
      this.sortFilter.setFilterData([]);
      this.setState({ fetchedCodes: [], costcodes: [], archivedLoaded: false });
      return;
    }
    const res = await request({
      call: axios.post('/costcodes/projects', {
        projectIds: [project.id],
        includeCategoryIds: true,
        ...(archivedOnly && { archivedOnly: true }),
        columns: [
          'description',
          'hourlyWage',
          'wageMultiplier',
          'dailyWage',
          'wageAdjustment',
          'hourlyBillingRate',
          'dailyBillingRate',
          'intuitId',
          'sageId',
          'eclipseId',
        ],
      }),
      errMsg: 'Could not get project cost codes',
      hideSuccessToast: true,
    });

    const {
      fetchedCodes,
    } = this.state;
    const incoming = res?.data ?? [];
    const fetched = archivedOnly
      ? [...(fetchedCodes.filter((c) => c.active)), ...incoming]
      : incoming;
    this.sortFilter.setFilterData(fetched);
    this.setState({ fetchedCodes: fetched, archivedLoaded: archivedOnly || false }, () => {
      this.sortFilter.applyPersistedSort();
    });
  }

  async fetchGlobalCodesForDivision(divisionId) {
    if (!divisionId) return;
    const res = await request({
      call: axios.get('/costcodes/global', {
        params: {
          divisionIds: [divisionId],
          includeCategoryIds: true,
          columns: [
            'description',
            'hourlyWage',
            'wageMultiplier',
            'dailyWage',
            'wageAdjustment',
            'hourlyBillingRate',
            'dailyBillingRate',
            'intuitId',
          ],
        },
      }),
      errMsg: 'Could not get global cost codes',
      hideSuccessToast: true,
    });
    this.setState({ globalCodes: res?.data ?? [] });
  }

  searchList(e) {
    const searchTerm = e.target.value.toLowerCase();
    this.setState({
      projects: search({
        data: this.projects,
        value: searchTerm,
        getField: (project) => (
          project.number
            ? `${project.number} - ${project.name}`
            : project.name),
      }),
    });
  }

  projectSelected(project) {
    this.sortFilter.resetFilters();
    this.setState({
      selectedProject: project,
      archivedLoaded: false,
    });
    this.fetchProjectCostcodes(project);
    this.fetchGlobalCodesForDivision(project?.divisionId);
  }

  createCostCodeWithProject(values) {
    const {
      createCostCodes,
    } = this.props;
    const {
      selectedProject,
      customFields,
    } = this.state;
    if (!selectedProject) {
      console.error('No Project selected', this.state);
    }
    Analytics.track('Costcode/Create/ProjectSpecific');

    let newValues;
    if (values && values instanceof Array) {
      newValues = values.map((val) => ({ ...val, divisionId: selectedProject.divisionId }));
    } else if (values) {
      const payload = formatCustomFieldCreatePayload({
        payload: values,
        customFields,
      });
      if (payload?.error || !payload) {
        return this.setState({
          errors: payload?.errorMap,
        });
      }

      newValues = payload;
    }

    this.setState({
      errors: {},
    });

    return createCostCodes(newValues, selectedProject.id)
      .then((success) => {
        if (success) this.refetch();
        return success;
      });
  }

  async update(id, payload) {
    const {
      customFields,
      costcodes,
    } = this.state;

    const { updateCostCode } = this.props;

    const res = await getCostcodeUpdatePayload({
      id,
      payload,
      customFields,
      costcodes,
      templates: [], // only check against global costcodes
    });

    if (res?.error || !res) {
      return this.setState({
        errors: res?.errorMap,
      });
    }

    this.setState({
      errors: {},
    });

    const result = await updateCostCode(id, res);
    this.refetch();
    return result;
  }

  archiveCostcode(costcode) {
    const {
      archiveCostCode,
    } = this.props;
    return new Promise((resolve) => {
      const mode = costcode.active ? 'Archive' : 'Activate';

      CustomConfirmModal({
        title: `${mode} Cost Code ${costcode.code}- ${costcode.name}?`,
        okText: mode,
        cancelText: 'Cancel',
        async onOk() {
          const result = await archiveCostCode(costcode.id, !costcode.active);
          resolve(result);
        },
        onCancel() {
          resolve();
        },
      });
    }).then((result) => {
      if (result !== undefined) this.refetch();
      return result;
    });
  }

  deleteCostcode(costcode) {
    const {
      deleteCostCodeById,
    } = this.props;
    return new Promise((resolve) => {
      CustomConfirmModal({
        title: `Delete Cost Code ${costcode.code}- ${costcode.name}?`,
        content: (
          <div>
            <WarningHeader justify="center" />
            <br />
            Deleting a cost code will unlink all tracked entries for that cost code
          </div>
        ),
        okText: 'Delete',
        cancelText: 'Cancel',
        async onOk() {
          const result = await deleteCostCodeById(costcode.id);
          resolve(result);
        },
        onCancel() {
          resolve();
        },
      });
    }).then((result) => {
      if (result !== undefined) this.refetch();
      return result;
    });
  }

  onBulkArchive = async (ids, { password }) => {
    const { bulkArchiveCostCodes: bulkArchive } = this.props;
    const result = await bulkArchive(ids, { password }, false);
    if (result) this.refetch();
    return result;
  }

  onBulkActivate = async (ids, { password }) => {
    const { bulkArchiveCostCodes: bulkArchive } = this.props;
    const result = await bulkArchive(ids, { password }, true);
    if (result) this.refetch();
    return result;
  }

  onBulkDelete = async (ids, { password }) => {
    const { bulkDeleteCostCodes: bulkDelete } = this.props;
    const result = await bulkDelete(ids, { password });
    if (result) this.refetch();
    return result;
  }

  export(type) {
    const {
      costcodes = [],
    } = this.state;
    const {
      divisions = {},
      categoriesIdMap = {},
    } = this.props;
    const workbook = XLSX.utils.book_new();
    const formattedCostcodes = costcodes.map((costcode) => {
      const { [costcode.divisionId]: { name: division } = {} } = divisions;
      const categoryNames = costcode.categoryIds
        ?.map((categoryId) => categoriesIdMap[categoryId]?.name)
        ?.join(', ');
      return {
        Name: costcode.name,
        'Cost Code': costcode.code,
        Description: costcode.description,
        Categories: categoryNames,
        Division: division,
      };
    });
    const sheet = XLSX.utils.json_to_sheet(formattedCostcodes, {
      header: ['Name', 'Cost Code', 'Description', 'Division', 'Categories'],
    });
    XLSX.utils.book_append_sheet(workbook, sheet, 'Cost Codes');
    XLSX.writeFile(workbook, `OntraccrCostCodes.${type}`);
  }

  exportCSV() {
    this.export('csv');
  }

  exportExcel() {
    this.export('xlsx');
  }

  getMoreOptions() {
    return [
      {
        title: 'Export to Excel',
        icon: 'file-excel',
        onClick: this.onExportExcel,
      },
      {
        title: 'Export to CSV',
        icon: 'file-text',
        onClick: this.onExportCSV,
      },
    ];
  }

  updateProjectState(projects, selectedDivisionIds) {
    const {
      selectedProject: {
        divisionId: selectedProjectDivisionId,
      } = {},
    } = this.state;

    const filteredProjects = projects.filter(({ divisionId }) => (
      selectedDivisionIds.has(divisionId)
    ));
    this.projects = filteredProjects;
    this.setState({
      projects: filteredProjects,
      selectedDivisionIds,
    });

    if (!selectedDivisionIds.has(selectedProjectDivisionId)) {
      const [project] = filteredProjects;
      this.projectSelected(project);
    }
  }

  refetch() {
    const { archivedLoaded, selectedProject } = this.state;
    this.fetchProjectCostcodes(selectedProject, { archivedOnly: archivedLoaded });
  }

  render() {
    const {
      projects = [],
      costcodes = [],
      globalCodes = [],
      selectedProject,
      errors = {},
      customFields = [],
      sortActive,
      activeFilters = {},
      filterIsActive,
    } = this.state;

    const {
      company: {
        settings: {
          suggestNumbers,
        } = {},
      } = {},
      categoriesIdMap = {},
      useRedesign = false,
    } = this.props;

    const selectedSet = new Set([]);

    if (selectedProject) {
      const { projects: stateProjects } = this.state;
      const ourProj = stateProjects.filter((project) => project.id === selectedProject.id);
      if (ourProj.length > 0) {
        selectedSet.add(ourProj[0]);
      }
    }

    const initialCode = suggestNumbers ? (getHighestNumber(costcodes, 'code') + 1).toString() : null;

    const addView = (form, formProps) => (
      <CostCodeAddView
        {
          ...{
            ...form,
            ...formProps,
            isProjectSpecific: true,
            costcodes: globalCodes,
            initialCode,
          }
        }
        errors={errors}
        customFields={customFields}
        setCustomFields={(fields) => this.setState({ customFields: fields })}
      />
    );

    const editView = (form, formProps) => (
      <CostCodeEditView
        form={form}
        formProps={formProps}
        isProjectSpecific
        errors={errors}
        customFields={customFields}
        setCustomFields={(fields) => this.setState({ customFields: fields })}
      />
    );

    return (
      <Row style={{ height: '100%', width: '100%' }}>
        <Col span={6} style={{ height: '100%', paddingRight: 10 }}>
          <SideList
            id="project-specific-costcodes"
            key="project-specific-costcodes"
            header={(
              <SideListSearchHeader
                onChange={this.onSearch}
              />
            )}
            dataSource={projects.filter((proj) => proj.active)}
            renderItem={renderItem}
            onClick={this.onProjectSelected}
            selectedItems={selectedSet}
            rowHeight={30}
            scrollable
          />
        </Col>
        <Col span={18} style={{ height: '100%', paddingLeft: 10 }}>
          <CardGrid
            onRef={(ref) => { this.cardRef = ref; }}
            dataSource={costcodes}
            itemView={CostCodeCard}
            itemDimensions={{ width: 239, height: 190 }}
            colCount={3}
            pageSize={6}
            add={{
              title: 'Enter Cost Code Details',
              formView: addView,
              width: 'fit-content',
              onClose: this.onCreateCostCode,
              validateSteps: FIELDS_BY_STEP,
            }}
            edit={{
              title: 'Edit Cost Code Details',
              formView: editView,
              width: 800,
              onClose: this.onUpdate,
              canEdit: () => Permissions.has('COSTCODES_WRITE'),
              canArchive: () => Permissions.has('COSTCODES_WRITE'),
              canDelete: () => Permissions.has('COSTCODES_WRITE'),
              validateSteps: FIELDS_BY_STEP,
            }}
            onArchive={this.onArchive}
            onDelete={this.onDelete}
            bulkActions={Permissions.has('COSTCODES_WRITE') ? {
              canArchive: true,
              canActivate: true,
              canDelete: true,
              entityLabel: 'cost codes',
              warnings: {
                delete: {
                  header: 'Deleting cost codes will uncode all time entries currently tied to this cost code.',
                  subtext: 'Only archived cost codes will be deleted. This action is permanent.',
                },
              },
            } : null}
            onBulkArchive={this.onBulkArchive}
            onBulkActivate={this.onBulkActivate}
            onBulkDelete={this.onBulkDelete}
            search={{
              customRanking: searchCostcodes,
              tieBreaker: searchTieBreaker,
            }}
            more={this.getMoreOptions()}
            sort={{
              sortOptions: this.sortFilter.sortOptions,
              sortActive,
            }}
            filter={(
              <FilterDropdown
                filters={[getFilterData(categoriesIdMap)]}
                activeFilters={activeFilters}
                onFilter={(checkedFilters, filterCategory) => (
                  this.sortFilter.filterCostcodes(checkedFilters, filterCategory)
                )}
                useRedesign={useRedesign}
              />
            )}
            filterActive={filterIsActive}
            isListView
            displayColumns={columnsToDisplay(categoriesIdMap)}
            title="Project Cost Codes"
            entity="Cost Code"
          />
        </Col>
      </Row>
    );
  }
}

export default withTranslation()(
  connect((state, ownProps) => ({
    ...ownProps,
    nux: state.nux.nux,
    company: state.settings.company,
    divisions: state.settings.divisions,
    selectedDivisionIds: state.settings.selectedDivisions,
    categoriesIdMap: state.costcodes.categoriesIdMap,
    useRedesign: state.settings.company?.settings?.uxRedesignPt1,
  }), {
    createCostCodes,
    updateCostCode,
    archiveCostCode,
    bulkArchiveCostCodes,
    deleteCostCodeById,
    bulkDeleteCostCodes,
    createNuxEntry,
  })(withRouter(CostCodeProjects)),
);
